import { Box, Stack } from '@mui/material'
import type { Theme } from '@mui/material/styles'
import { useTheme } from '@mui/material/styles'
import { visuallyHidden } from '@mui/utils'
import type { TFunction } from 'i18next'
import type { ComponentProps, KeyboardEvent } from 'react'
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import PortFieldPanel from '@/features/resource/components/PortFieldPanel'
import PortGraphLegend from '@/features/resource/components/PortGraphLegend'
import PortGraphNodeShape, {
  NODE_LABEL_CLIP_RECT,
  NODE_LABEL_CLIP_SUFFIX,
  portEdgeColor,
} from '@/features/resource/components/PortGraphNode'
import { getPortTypeDescriptor } from '@/features/resource/components/PortTypeChip'
import useTruncatedSvgText from '@/features/resource/hooks/useTruncatedSvgText'
import { targetControllerLabel } from '@/features/resource/utils/controllerLabel'
import type { PortGraphBand, PortGraphEdge } from '@/features/resource/utils/portGraph'
import {
  buildPortGraph,
  CROSS_TAG_HEIGHT,
  CROSS_TAG_TEXT_WIDTH,
  CROSS_TAG_WIDTH,
  GUTTER_LABEL_INSET,
  GUTTER_LABEL_WIDTH,
  GUTTER_LABEL_X,
  MIN_SVG_FONT_SIZE,
  relatedPortIds,
} from '@/features/resource/utils/portGraph'
import type { ControllerGroup, GroupedPort, PortReference } from '@/features/resource/utils/portGroups'

/** Nothing narrower than this reads; below it the stage scrolls rather than crushing the columns. */
const MIN_STAGE_WIDTH = 700

/** Per-instance id for a node's `<g>`, so Escape from the panel can put focus back on it. */
function portNodeElementId(idPrefix: string, portId: string): string {
  return `${idPrefix}-node-${portId}`
}

const FADED_OPACITY = 0.22

/**
 * How a controller names itself in the gutter, in priority order.
 *
 * A controller name only ever reaches the API through a metering port, so a controller contributing
 * only control ports to a resource has an equipment number and no name — guaranteed by the model
 * rather than an edge case. When that happens the equipment number is promoted into the headline and
 * set in mono, so it reads as an identifier rather than as a name. That is the same fallback the
 * table already makes with `ports_table_label_by_equipment_number`.
 */
function controllerHeadline(group: ControllerGroup, t: TFunction<'translation'>) {
  if (group.equipmentNumber === null) {
    return {
      primary: t('resources.detail_panel.controller_group_unknown'),
      isIdentifier: false,
      isAbsent: true,
      secondary: [t('resources.detail_panel.ports_graph_controller_no_identifier')],
    }
  }

  const imei =
    group.controllerImei === null
      ? []
      : [t('resources.detail_panel.controller_group_imei', { imei: group.controllerImei })]

  if (group.controllerName === null) {
    return {
      primary: t('resources.detail_panel.controller_group_named_by_equipment_number', {
        equipmentNumber: group.equipmentNumber,
      }),
      isIdentifier: true,
      isAbsent: false,
      secondary: [...imei, t('resources.detail_panel.controller_group_no_name')],
    }
  }

  return {
    primary: group.controllerName,
    isIdentifier: false,
    isAbsent: false,
    secondary: [
      t('resources.detail_panel.ports_graph_controller_equipment_number_short', {
        equipmentNumber: group.equipmentNumber,
      }),
      ...imei,
    ],
  }
}

/** The port's type as the table words it, flattened onto the one line a node has room for. */
function nodeTypeLabel(port: GroupedPort, t: TFunction<'translation'>): string {
  const { label, qualifier } = getPortTypeDescriptor(port, t)

  return qualifier === null ? label : `${label} · ${qualifier}`
}

function meteringSecondaryLabel(port: GroupedPort, t: TFunction<'translation'>): string | undefined {
  if (port.kind !== 'METERING') {
    return undefined
  }

  // Same reading as the Settings column: anything that is not MINUS is additive.
  return port.meteringPort.arithmeticOperation === 'MINUS'
    ? `− ${t('resources.metering_port.relation.subtractive')}`
    : `+ ${t('resources.metering_port.relation.additive')}`
}

/**
 * What a node announces.
 *
 * The relation is spoken as part of the name rather than left to the drawing. An SVG graph has no
 * equivalent of the table's per-group accessible name and its "on controller X" sentence, and the
 * relation is the one thing this view exists to show — leaving it visual only would make the graph
 * a strictly worse view for anyone not reading it with their eyes.
 */
/**
 * Above this many relations, naming them stops helping.
 *
 * A spoken name is read start to finish with no way to skim, so fifteen port names in one sentence
 * is worse than useless — the count is the fact that can be acted on, and the names are a jump away
 * in the panel. Five is a judgement about listening to a sentence, not a property of the data.
 */
const MANY_RELATIONS = 5

/**
 * The related ports, named where they have names and counted where they do not.
 *
 * A port with no name would otherwise contribute a bare UUID, which is a long thing to listen to
 * and tells the reader nothing; several of them make the sentence unusable. The same goes for ones
 * this resource does not list, which are grouped rather than enumerated for the same reason.
 */
function relationSummary(references: readonly PortReference[], t: TFunction<'translation'>): string {
  const named = references.flatMap((reference) =>
    reference.targetIsListed && reference.targetPortName !== null ? [reference.targetPortName] : [],
  )
  const unnamed = references.filter((reference) => reference.targetIsListed && reference.targetPortName === null).length
  const unlisted = references.filter((reference) => !reference.targetIsListed).length

  return [
    ...named,
    ...(unnamed > 0 ? [t('resources.detail_panel.ports_graph_relation_unnamed', { count: unnamed })] : []),
    ...(unlisted > 0 ? [t('resources.detail_panel.ports_graph_relation_unlisted', { count: unlisted })] : []),
  ].join(', ')
}

function nodeAccessibleName(
  port: GroupedPort,
  group: ControllerGroup,
  references: readonly PortReference[],
  t: TFunction<'translation'>,
): string {
  const relation = (() => {
    if (port.kind === 'CONTROL') {
      const [reference] = references
      if (reference === undefined) {
        return t('resources.detail_panel.ports_graph_measured_by_nothing')
      }

      /*
       * A control port can be measured by many meters. Several of them name the ports and stop
       * there: the controller each one sits on is on its own node and in the panel, and repeating
       * it per meter turns one spoken sentence into a paragraph. Past a handful even the names stop
       * earning their place, and the count is what is left worth saying — fifteen meters is a real
       * resource, not a hypothetical.
       */
      if (references.length > MANY_RELATIONS) {
        return t('resources.detail_panel.ports_graph_measured_by_count', { count: references.length })
      }

      if (references.length > 1) {
        return t('resources.detail_panel.ports_graph_measured_by_several', {
          portNames: relationSummary(references, t),
        })
      }

      if (!reference.targetIsListed) {
        return t('resources.detail_panel.ports_graph_measured_by_unlisted', { portId: reference.targetPortId })
      }

      const portName = reference.targetPortName ?? reference.targetPortId

      return reference.isCrossController
        ? t('resources.detail_panel.ports_graph_measured_by_on_other_controller', {
            portName,
            controllerName: targetControllerLabel(reference, t),
          })
        : t('resources.detail_panel.ports_graph_measured_by', { portName })
    }

    if (references.length === 0) {
      return t('resources.detail_panel.ports_graph_measures_nothing')
    }

    // The same treatment as the control side above, for the same reason: one relation is read the
    // same way whichever end of it you are standing on.
    if (references.length > MANY_RELATIONS) {
      return t('resources.detail_panel.ports_graph_measures_count', { count: references.length })
    }

    return t('resources.detail_panel.ports_graph_measures', { portNames: relationSummary(references, t) })
  })()

  return t('resources.detail_panel.ports_graph_node_label', {
    portType: nodeTypeLabel(port, t),
    portName: port.name ?? t('resources.detail_panel.ports_graph_unnamed_port'),
    controllerName: controllerHeadline(group, t).primary,
    relation,
  })
}

/**
 * One line of a band's gutter label, cut with an ellipsis when it does not fit. Its own component
 * because each line measures itself and the number of lines varies, so a hook called in a loop
 * would change count between renders.
 *
 * The `<title>` hangs off the wrapping group rather than the `<text>`, since measuring writes
 * candidates into the text element and would take any child with them. `aria-label` keeps the full
 * string spoken, so the cut is visual only.
 *
 * That group is `role="presentation"`, or the `<title>` would make it a named group of its own and
 * the name would be announced twice: once for the group and once for the text inside it. A native
 * tooltip is resolved by walking the DOM, not the accessibility tree, so hiding the group from the
 * latter costs the hover nothing.
 */
function GutterLabelLine({ text, y, ...textProps }: Readonly<{ text: string; y: number } & ComponentProps<'text'>>) {
  const { ref, displayText, isTruncated } = useTruncatedSvgText(text, GUTTER_LABEL_WIDTH)

  return (
    <g role="presentation">
      {isTruncated && <title>{text}</title>}
      <text
        ref={ref}
        aria-label={isTruncated ? text : undefined}
        textAnchor="end"
        x={GUTTER_LABEL_X}
        y={y}
        {...textProps}
      >
        {displayText}
      </text>
    </g>
  )
}

function GutterLabel({
  band,
  theme,
  t,
  clipId,
}: Readonly<{ band: PortGraphBand; theme: Theme; t: TFunction<'translation'>; clipId: string }>) {
  const headline = controllerHeadline(band.group, t)
  const centre = band.y + band.height / 2
  const top = centre - headline.secondary.length * 6.5

  return (
    <g clipPath={`url(#${clipId})`}>
      <GutterLabelLine
        fill={theme.palette.text.primary}
        fillOpacity={headline.isAbsent ? 0.6 : 1}
        fontFamily={headline.isIdentifier ? 'monospace' : undefined}
        fontSize={headline.isIdentifier ? MIN_SVG_FONT_SIZE : 12.5}
        fontStyle={headline.isAbsent ? 'italic' : undefined}
        fontWeight={600}
        text={headline.primary}
        y={top}
      />
      {headline.secondary.map((line, index) => (
        <GutterLabelLine
          key={line}
          fill={theme.palette.text.secondary}
          fontFamily="monospace"
          fontSize={MIN_SVG_FONT_SIZE}
          text={line}
          y={top + 14 + index * 12}
        />
      ))}
    </g>
  )
}

export type ResourcePortsGraphProps = {
  groups: readonly ControllerGroup[]
  /**
   * False for a LOCAL resource, whose port error fields froze when the controller left monolith
   * polling. Gates both of this view's error surfaces: the node's dot and the panel's errors row.
   */
  hasLiveDeviceErrors: boolean
  /** Computed once by the section and shared with the table, so no relation is resolved twice. */
  referencesByPortId: ReadonlyMap<string, PortReference[]>
}

/**
 * The resource's ports as a graph: ports are nodes, the measurement relation is an edge, and each
 * controller is a labelled band containing its own ports.
 *
 * Containment is drawn rather than tabulated, which is the point — a relation that leaves its
 * controller is visibly seen to leave its band, where the table can only annotate it with an arrow
 * and a caption. That relation is permitted by the data model, so it is drawn as notable and never
 * as wrong: inked and dashed, tagged, in no warning or error hue.
 */
export default function ResourcePortsGraph({
  groups,
  referencesByPortId,
  hasLiveDeviceErrors,
}: Readonly<ResourcePortsGraphProps>) {
  const { t } = useTranslation()
  const theme = useTheme()

  const [selectedPortId, setSelectedPortId] = useState<string | null>(null)

  /*
   * Hover and focus are two input modes and get two pieces of state, with focus taking precedence.
   *
   * One variable for both let the mouse cancel the keyboard: moving off a node that had been hovered
   * cleared the isolation belonging to whichever node currently held focus, which for a keyboard
   * user is their context disappearing because a pointer moved somewhere they cannot see.
   */
  const [focusedPortId, setFocusedPortId] = useState<string | null>(null)
  const [hoveredPortId, setHoveredPortId] = useState<string | null>(null)
  const isolatedPortId = focusedPortId ?? hoveredPortId

  /*
   * SVG ids live in one document-wide namespace, so these are scoped per instance: two graphs on a
   * page would otherwise both define `port-graph-arrow`, and every edge in the second would resolve
   * its marker against the first one's definition. The table needs no equivalent — its row ids are
   * port UUIDs, unique on their own — so this is the same hazard rather than the same mechanism.
   */
  const idPrefix = useId()
  const arrowMarkerId = `${idPrefix}-arrow`
  const crossingArrowMarkerId = `${idPrefix}-arrow-crossing`
  const gutterClipId = `${idPrefix}-gutter-clip`
  const nodeLabelClipId = `${idPrefix}-${NODE_LABEL_CLIP_SUFFIX}`

  const graph = useMemo(() => buildPortGraph(groups, referencesByPortId), [groups, referencesByPortId])

  const groupByPortId = useMemo(
    () => new Map(groups.flatMap((group) => group.ports.map((port) => [port.id, group] as const))),
    [groups],
  )

  const portIdsOnStage = useMemo(() => new Set(graph.nodes.map((node) => node.port.id)), [graph.nodes])

  /*
   * Nothing is isolated on behalf of a port that is no longer drawn.
   *
   * `relatedPortIds` of a missing id answers with that id alone, since no edge touches it — so every
   * node still on the stage falls outside the set and the whole graph fades at once. A poll removing
   * the hovered or focused port fires neither mouseleave nor blur, so the id would otherwise sit
   * there until the reader moved the pointer or pressed Tab.
   */
  const isolated = useMemo(
    () =>
      isolatedPortId === null || !portIdsOnStage.has(isolatedPortId)
        ? null
        : relatedPortIds(isolatedPortId, graph.edges),
    [isolatedPortId, portIdsOnStage, graph.edges],
  )

  /*
   * One edge is in play when both of its ends are. The tag on a boundary follows the boundary
   * rather than the edge that happens to carry it: only the first crossing through a boundary holds
   * a tag, so isolating any of the others faded the only tag there was and left the highlighted
   * crossing unlabelled. It stays while anything still crosses there, and goes when nothing does.
   */
  const isEdgeVisible = (edge: PortGraphEdge) =>
    isolated === null || (isolated.has(edge.fromPortId) && isolated.has(edge.toPortId))

  const boundariesInPlay = new Set(
    graph.edges.flatMap((edge) =>
      edge.crossingBoundary !== null && isEdgeVisible(edge) ? [edge.crossingBoundary] : [],
    ),
  )

  const selectedNode = graph.nodes.find((node) => node.port.id === selectedPortId) ?? null

  /*
   * And the ids themselves go, rather than being skipped over on every render. A selection left
   * behind puts the panel on its empty state while the graph still believes something is selected,
   * and silently re-selects the port if a later poll brings it back.
   */
  useEffect(() => {
    if (selectedPortId !== null && selectedNode === null) {
      setSelectedPortId(null)
    }
    if (focusedPortId !== null && !portIdsOnStage.has(focusedPortId)) {
      setFocusedPortId(null)
    }
    if (hoveredPortId !== null && !portIdsOnStage.has(hoveredPortId)) {
      setHoveredPortId(null)
    }
  }, [selectedPortId, selectedNode, focusedPortId, hoveredPortId, portIdsOnStage])

  const controlPortCount = graph.nodes.filter((node) => node.port.kind === 'CONTROL').length
  const hasCrossControllerEdge = graph.edges.some((edge) => edge.reference.isCrossController)
  const hasUnidentifiedController = graph.bands.some((band) => band.group.equipmentNumber === null)

  const fadedSx = {
    transition: 'opacity 140ms ease',
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
  }

  /*
   * A jump taken from inside the field panel moves focus to the panel's heading, which names the
   * port just moved to. The button that was pressed unmounts on the way — a port does not reference
   * itself, so it is not in the new port's list — and focus fell to the body, losing a keyboard
   * reader's place entirely. Selecting a node instead leaves focus on the node, which is where that
   * reader already is.
   */
  const panelHeadingRef = useRef<HTMLElement | null>(null)
  const pendingPanelFocus = useRef(false)

  useEffect(() => {
    if (pendingPanelFocus.current) {
      pendingPanelFocus.current = false
      panelHeadingRef.current?.focus()
    }
  }, [selectedPortId])

  const selectFromPanel = useCallback((portId: string) => {
    pendingPanelFocus.current = true
    setSelectedPortId(portId)
  }, [])

  // Escape from inside the panel clears the selection and puts the reader back on the node it was
  // about, rather than leaving focus on a heading that has just been replaced by the empty state.
  const dismissFromPanel = useCallback(() => {
    const node = selectedPortId === null ? null : document.getElementById(portNodeElementId(idPrefix, selectedPortId))
    setSelectedPortId(null)
    node?.focus()
  }, [selectedPortId, idPrefix])

  // Activating the selected node again clears it, so a selection can be undone by the same gesture
  // that made it — there is otherwise no way back to an empty panel once one is opened.
  const toggleSelectedPort = (portId: string) => setSelectedPortId((current) => (current === portId ? null : portId))

  /*
   * Whether the focus about to arrive came from a pointer. A node is `tabIndex={0}`, so clicking one
   * focuses it and focus outranks hover — which left a mouse user stuck, hovering showing nothing
   * after a click until something else took focus.
   *
   * Set on mousedown and cleared by whatever happens next: the focus that follows, a key press, or
   * the pointer coming up anywhere. The last two matter because neither is guaranteed to reach the
   * node — a mousedown on a node that already holds focus fires no focus event, and a drag released
   * off it fires no mouseup. Left set, the flag swallows the next keyboard focus and leaves that
   * node with no ring at all.
   */
  const focusCameFromPointer = useRef(false)

  useEffect(() => {
    const clear = () => {
      focusCameFromPointer.current = false
    }

    window.addEventListener('mouseup', clear)
    window.addEventListener('keydown', clear, true)

    return () => {
      window.removeEventListener('mouseup', clear)
      window.removeEventListener('keydown', clear, true)
    }
  }, [])

  const onNodeKeyDown = (event: KeyboardEvent<SVGGElement>, portId: string) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      toggleSelectedPort(portId)
    }

    if (event.key === 'Escape' && selectedPortId !== null) {
      event.preventDefault()
      setSelectedPortId(null)
    }
  }

  return (
    <Stack>
      <Box
        display="grid"
        gridTemplateColumns={{ xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) 300px' }}
        sx={{ alignItems: 'stretch' }}
      >
        {/*
         * `overflowY` is set explicitly because the two axes are not independent: per CSS Overflow,
         * a `visible` axis beside a scrolling one computes to `auto`, so `overflowX: 'auto'` alone
         * made this a vertical scroll container too and the graph grew a second scrollbar.
         *
         * What it scrolled was about a pixel. The SVG is `width: 100%` with `height: auto`, so its
         * height lands on a fractional pixel, `scrollHeight` rounds up while `clientHeight` rounds
         * down, and `auto` renders a full scrollbar for the difference.
         *
         * `hidden` cannot clip the drawing: the SVG is always its natural height at its current
         * width. The only other way it overflows is a horizontal scrollbar eating into
         * `clientHeight` below ~716px, and below the last band sit 8px of padding plus the half
         * band-gap of empty viewBox (~13.6 CSS px), so a ~15px scrollbar lands in empty space. That
         * budget is spent, not spare — shrinking `BAND_GAP` or this padding spends it.
         *
         * `overflowX` stays `auto` and is load-bearing: `minWidth` forces real horizontal overflow
         * below ~716px, 60px at a 640px stage and 200px at 500px.
         */}
        <Box paddingX={1} paddingY={1} sx={{ overflowX: 'auto', overflowY: 'hidden' }}>
          <Box
            aria-label={t('resources.detail_panel.ports_graph_label', {
              controlPortCount,
              meteringPortCount: graph.nodes.length - controlPortCount,
              relationCount: graph.edges.length,
              controllerCount: graph.bands.length,
            })}
            component="svg"
            role="group"
            sx={{ display: 'block', width: '100%', minWidth: `${MIN_STAGE_WIDTH}px`, height: 'auto' }}
            viewBox={`0 0 ${graph.width} ${graph.height}`}
          >
            <defs>
              <clipPath id={nodeLabelClipId}>
                <rect {...NODE_LABEL_CLIP_RECT} />
              </clipPath>
              <clipPath id={gutterClipId}>
                <rect height={graph.height} width={GUTTER_LABEL_WIDTH} x={GUTTER_LABEL_INSET} y={0} />
              </clipPath>
              <marker
                id={arrowMarkerId}
                markerHeight={7}
                markerWidth={7}
                orient="auto-start-reverse"
                refX={9}
                refY={5}
                viewBox="0 0 10 10"
              >
                <path d="M 0 1 L 9 5 L 0 9 z" fill={portEdgeColor(theme, false)} />
              </marker>
              <marker
                id={crossingArrowMarkerId}
                markerHeight={7.5}
                markerWidth={7.5}
                orient="auto-start-reverse"
                refX={9}
                refY={5}
                viewBox="0 0 10 10"
              >
                <path d="M 0 1 L 9 5 L 0 9 z" fill={portEdgeColor(theme, true)} />
              </marker>
            </defs>

            {graph.bands.map((band) => (
              <g key={band.group.equipmentNumber ?? 'unknown'} data-controller-band="">
                {/*
                 * Neutral rather than hued. The one hue channel a graph has is spent on port kind,
                 * and the controller gets a geometry instead, so the fill is the same
                 * `action.hover` the table's group header already uses — so the band is literally
                 * today's containment, drawn. A dashed, unfilled band is a different state, not a
                 * different controller: nothing identifies these ports.
                 */}
                <rect
                  fill={band.group.equipmentNumber === null ? 'none' : theme.palette.action.hover}
                  height={band.height}
                  rx={8}
                  stroke={theme.palette.divider}
                  strokeDasharray={band.group.equipmentNumber === null ? '6 4' : undefined}
                  strokeWidth={1}
                  width={band.width}
                  x={band.x}
                  y={band.y}
                />
                <GutterLabel band={band} clipId={gutterClipId} t={t} theme={theme} />
              </g>
            ))}

            {graph.edges.map((edge) => {
              const isCrossing = edge.reference.isCrossController
              const isVisible = isEdgeVisible(edge)

              return (
                <Box
                  key={`${edge.fromPortId}-${edge.toPortId}`}
                  component="path"
                  d={edge.path}
                  data-cross-controller={isCrossing}
                  data-port-edge=""
                  fill="none"
                  markerEnd={`url(#${isCrossing ? crossingArrowMarkerId : arrowMarkerId})`}
                  stroke={portEdgeColor(theme, isCrossing)}
                  strokeDasharray={isCrossing ? '7 4' : undefined}
                  strokeWidth={isCrossing ? 2 : 1.3}
                  sx={{ ...fadedSx, opacity: isVisible ? 1 : FADED_OPACITY }}
                />
              )
            })}

            {graph.edges.map((edge) =>
              edge.crossingTag === null ? null : (
                <Box
                  key={`tag-${edge.fromPortId}-${edge.toPortId}`}
                  component="g"
                  pointerEvents="none"
                  sx={{
                    ...fadedSx,
                    opacity:
                      edge.crossingBoundary !== null && boundariesInPlay.has(edge.crossingBoundary) ? 1 : FADED_OPACITY,
                  }}
                >
                  <rect
                    fill={theme.palette.background.paper}
                    height={CROSS_TAG_HEIGHT}
                    rx={CROSS_TAG_HEIGHT / 2}
                    stroke={portEdgeColor(theme, true)}
                    strokeWidth={1}
                    width={CROSS_TAG_WIDTH}
                    x={edge.crossingTag.x - CROSS_TAG_WIDTH / 2}
                    y={edge.crossingTag.y - CROSS_TAG_HEIGHT / 2}
                  />
                  <text
                    fill={theme.palette.text.primary}
                    fontFamily="monospace"
                    fontSize={MIN_SVG_FONT_SIZE}
                    // The width is set rather than hoped for: the badge is fixed and the clamp
                    // window is computed from it, so the text cannot be left to whatever advance
                    // ratio `monospace` resolves to. See CROSS_TAG_TEXT_WIDTH.
                    lengthAdjust="spacingAndGlyphs"
                    textAnchor="middle"
                    textLength={CROSS_TAG_TEXT_WIDTH}
                    x={edge.crossingTag.x}
                    y={edge.crossingTag.y + 3.5}
                  >
                    {t('resources.detail_panel.ports_graph_cross_controller_tag')}
                  </text>
                </Box>
              ),
            )}

            {graph.nodes.map((node) => {
              const group = groupByPortId.get(node.port.id)
              const references = referencesByPortId.get(node.port.id) ?? []
              const isSelected = node.port.id === selectedPortId
              const isFocused = node.port.id === focusedPortId

              return (
                <Box
                  key={node.port.id}
                  aria-label={group === undefined ? node.port.id : nodeAccessibleName(node.port, group, references, t)}
                  aria-pressed={isSelected}
                  component="g"
                  data-port-node=""
                  id={portNodeElementId(idPrefix, node.port.id)}
                  role="button"
                  sx={{
                    ...fadedSx,
                    cursor: 'pointer',
                    opacity: isolated === null || isolated.has(node.port.id) ? 1 : FADED_OPACITY,
                    // The ring below is the focus indicator, so the browser's own would double it.
                    '&:focus': { outline: 'none' },
                  }}
                  tabIndex={0}
                  transform={`translate(${node.x}, ${node.y})`}
                  onBlur={() => setFocusedPortId(null)}
                  onClick={() => toggleSelectedPort(node.port.id)}
                  onFocus={() => {
                    const fromPointer = focusCameFromPointer.current
                    focusCameFromPointer.current = false
                    if (!fromPointer) {
                      setFocusedPortId(node.port.id)
                    }
                  }}
                  onKeyDown={(event) => onNodeKeyDown(event, node.port.id)}
                  onMouseDown={() => {
                    focusCameFromPointer.current = true
                  }}
                  onMouseEnter={() => setHoveredPortId(node.port.id)}
                  onMouseLeave={() => setHoveredPortId(null)}
                >
                  <PortGraphNodeShape
                    hasLiveDeviceErrors={hasLiveDeviceErrors}
                    labelClipId={nodeLabelClipId}
                    port={node.port}
                    secondaryLabel={meteringSecondaryLabel(node.port, t)}
                    theme={theme}
                    typeLabel={nodeTypeLabel(node.port, t)}
                    unnamedLabel={t('resources.detail_panel.ports_graph_unnamed_port')}
                  />
                  {/*
                   * Two rings rather than one that changes shape, because the states co-occur: a
                   * single rect choosing its attributes by ternary let selection win every one of
                   * them, so a selected node drew identical markup before and after focus arrived.
                   *
                   * Both sit outside the node's own stroke so neither is mistaken for the port-kind
                   * hue, and focus sits further out so it reads as a ring around the selection.
                   */}
                  {isSelected && (
                    <rect
                      data-node-ring="selected"
                      fill="none"
                      height={node.height + 8}
                      rx={10}
                      stroke={theme.palette.primary.main}
                      strokeWidth={2}
                      width={node.width + 8}
                      x={-4}
                      y={-4}
                    />
                  )}
                  {isFocused && (
                    <rect
                      data-node-ring="focus"
                      fill="none"
                      height={node.height + 16}
                      rx={14}
                      stroke={theme.palette.primary.main}
                      strokeDasharray="5 3"
                      strokeWidth={2.5}
                      width={node.width + 16}
                      x={-8}
                      y={-8}
                    />
                  )}
                </Box>
              )
            })}
          </Box>
        </Box>

        <Box
          padding={2}
          sx={{
            borderTop: { xs: 1, md: 0 },
            borderLeft: { xs: 0, md: 1 },
            borderColor: { xs: 'divider', md: 'divider' },
          }}
        >
          <PortGraphLegend
            hasCrossControllerEdge={hasCrossControllerEdge}
            hasUnidentifiedController={hasUnidentifiedController}
          />
        </Box>
      </Box>

      {/*
       * `position: relative` is load-bearing, and not for anything visible. The live region below is
       * `visuallyHidden`, which is `position: absolute` relying on `clip` rather than on being
       * moved — and an absolutely positioned box is clipped only by ancestors in its containing
       * block chain. With no positioned ancestor it sat at its static position below the whole graph
       * and escaped the shell's `overflow: hidden`, extending the document past the fold: on a
       * 1238×959 viewport everything else capped at 959 while that box ended at 1314.
       *
       * It only shows up when the graph is taller than the viewport, which is why a sweep over
       * widths could not find it.
       */}
      <Box padding={2} sx={{ borderTop: 1, borderColor: 'divider', position: 'relative' }}>
        {/*
         * Selecting a node repaints this panel somewhere else on the page, with no focus move to
         * carry the reader there — the same gap the table closed by moving focus to the row it
         * jumped to. Focus is deliberately left on the node here, since the graph is navigated by
         * moving between nodes and yanking focus into the panel would break that; so the change is
         * announced instead of moved to.
         */}
        <Box aria-live="polite" sx={visuallyHidden}>
          {selectedNode === null
            ? ''
            : t('resources.detail_panel.ports_graph_selected_announcement', {
                portName: selectedNode.port.name ?? t('resources.detail_panel.ports_graph_unnamed_port'),
              })}
        </Box>
        <PortFieldPanel
          hasLiveDeviceErrors={hasLiveDeviceErrors}
          headingRef={panelHeadingRef}
          port={selectedNode?.port ?? null}
          references={selectedPortId === null ? [] : (referencesByPortId.get(selectedPortId) ?? [])}
          onDismiss={dismissFromPanel}
          onSelectPort={selectFromPanel}
        />
      </Box>
    </Stack>
  )
}
